import type { ReadBuyer } from '@tinct/core-api-models';

/**
 * The buyer brandmark-logo variant key for a greyscale/dark combination.
 */
export function buyerLogoVariantKey(
  options: { greyscale?: boolean; dark?: boolean } = {},
):
  | 'brandmarkLogo'
  | 'brandmarkLogoGreyscale'
  | 'brandmarkLogoDark'
  | 'brandmarkLogoGreyscaleDark' {
  const { greyscale = false, dark = false } = options;
  return `brandmarkLogo${greyscale ? 'Greyscale' : ''}${
    dark ? 'Dark' : ''
  }` as const;
}

/**
 * Resolves the brandmark logo URL for a buyer, honoring the block's
 * greyscale/dark rendering flags. The buyer's own logo variant wins; when the
 * buyer doesn't carry that combination the company's variant is used instead.
 * Returns null when neither has the requested combination.
 */
export function getBuyerLogoUrl(
  buyer: ReadBuyer,
  options: { greyscale?: boolean; dark?: boolean } = {},
): string | null {
  const key = buyerLogoVariantKey(options);
  return buyer[key]?.url ?? buyer.company?.[key]?.url ?? null;
}

const FILL_LOGO_KEYS = [
  'brandmarkLogo',
  'brandmarkLogoGreyscale',
  'brandmarkLogoDark',
  'brandmarkLogoGreyscaleDark',
  'logo',
  'logoGreyscale',
] as const;

/**
 * Resolves the base logo used for fill-color (silhouette) rendering. The fill
 * repaints every pixel, so only the alpha shape matters — any variant works,
 * and the first resolvable one wins (buyer override, then company), brandmark
 * variants before full logos. Returns null when the buyer has no logo at all.
 */
export function getBuyerFillLogoUrl(buyer: ReadBuyer): string | null {
  for (const key of FILL_LOGO_KEYS) {
    const url = buyer[key]?.url ?? buyer.company?.[key]?.url;
    if (url) return url;
  }
  return null;
}
